import React from 'react';
import styled from 'styled-components';
import { Container } from '../styles/components';
import CTAButton from './CTAButton';
import SectionNumber from './SectionNumber';


const HeroSection = styled.section`
  min-height: 75svh;
  height: auto;
  padding-top: 150px;
  position: relative;
  overflow: visible;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: ${props => props.theme.colors.charcoal};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/assets/patterns/Contours - clipped (dark).svg');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    opacity: 1;
    z-index: 1;
  }
`;

const HeroContainer = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxl};
  align-items: center;
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  /* padding: 0; Removed to allow default Container padding */
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  padding-right: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-right: 0;
    /* Remove manual padding if container padding is sufficient, but keeping for safety on small screens 
       or adjusting to avoid double padding if needed. 
       Actually, with Container padding restored (32px or 16px), we might not need extra padding here 
       or we can keep it for extra breathing room. 
       Let's reduce it to 0 horizontal if container handles it? 
       On mobile: Container=16px. LeftColumn=16px. Total 32px. Good.
       On tablet: Container=32px. LeftColumn=16px. Total 48px. Good.
       I will keep it. */
    padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.md};
  }
`;

const Heading = styled.h1`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-weight: ${props => props.theme.typography.heading.fontWeight};
  font-size: 2.5rem;
  line-height: 1.1;
  color: ${props => props.theme.colors.bone};
  margin: 0;
  max-width: 24ch;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Rule = styled.hr`
  width: 20%;
  height: 0.75px;
  background-color: ${props => props.theme.colors.bone};
  border: none;
  margin: 0;
`;

const SubHeading = styled.p`
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: ${props => props.theme.typography.sizes.bodyLarge}; // 20px
  line-height: 1.5;
  color: ${props => props.theme.colors.bone};
  margin: 0;
  font-weight: 400;
`;


const ButtonGroup = styled.div`
  display: flex;
  margin: 40px 0 0 0;
  gap: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column !important;
    width: auto;
    align-items: flex-start;
    
    & > * {
      width: auto !important;
      text-align: center;
      margin-bottom: ${props => props.theme.spacing.sm};
    }
  }
`;

const RightColumn = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    /* order: -1; Removed to keep text first */
    width: 100%;
    margin-top: ${props => props.theme.spacing.lg};
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  max-width: 550px; /* Increased from 450px */
  object-fit: contain;
  /* margin-bottom: -6%; Removed in favor of transform */
  transform: translateY(8%); /* Push image down to overlap the next section */

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    transform: none; /* Reset on tablet/mobile if needed, or keep it */
    margin-bottom: 0;
    max-width: 80% !important; 
    margin-top: 2rem;
  }
`;

const Hero = () => {
  const handleBookCallClick = () => {
    // Open Calendly popup widget
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/ellierofe/intro-chat?hide_event_type_details=1&hide_gdpr_banner=1&text_color=231f20&primary_color=c8553d'
      });
    }
  };

  return (
    <HeroSection id="hero">
      <HeroContainer>
        <LeftColumn>
          <Heading>
            Your work deserves better than a deck that gets binned before lunch
          </Heading>
          <Rule />
          <SubHeading>
            You're building something that could change lives.
            But if investors can't see the value, none of it matters.
          </SubHeading>
          <SubHeading>
            Let's fix the story to sell what you've built - starting with strategy, not slides.
          </SubHeading>
          <ButtonGroup>
            <CTAButton onClick={handleBookCallClick}>
              Book a strategy call
            </CTAButton>
          </ButtonGroup>
        </LeftColumn>

        <RightColumn>
          <HeroImage
            src="/assets/ellie-hero-green.png"
            alt="Eleanor Rofe - Pitch Strategy Consultant"
          />
        </RightColumn>
      </HeroContainer>

      <SectionNumber number={1} />
    </HeroSection>
  );
};

export default Hero; 